// KPI cards of the results column, as render() fills #statPill … #statVerificaPill: label, value (with the unit in a
// <small>), formula (report module) and the ok / bad tone. Labels translated, values formatted as the HTML.
import type { Translation } from "../../../hooks/useTranslations";
import {
  formulaCaricoCombinato,
  formulaFaxRdUnione,
  formulaForceF,
  formulaFvRdUnione,
  formulaFvRkUnione,
  formulaFvRkVite,
  formulaKser,
  formulaNef,
  formulaNefShear,
} from "./reportFormulas";
import type { CalcState, EngineTexts } from "../../../http/screwConnector/formulas";
import { governingLabel } from "../../../http/screwConnector/formulas/resistance";

type StatsT = Translation["screwConnectorModule"]["calculation"]["stats"];

export type ResultRow = { labelHtml: string; valueHtml: string; formulaHtml?: string };
// rows[0] is the card's main value, the others the extra rows (n_ef shear, extraction / splitting checks)
export type ResultCard = { key: string; tone: "ok" | "bad" | null; rows: ResultRow[] };

const small = (text: string) => `<small>${text}</small>`;
const unitSpan = (text: string) => ` <span class="unit">${text}</span>`;

function pct(t: StatsT, eta: number, ok: boolean): string {
  return `${(eta * 100).toFixed(0)}%${small(` ${ok ? t.verified : t.notVerified}`)}`;
}

// cards before the first calculation (markup defaults)
export function placeholderResultCards(t: StatsT): [ResultCard[], ResultCard[]] {
  const empty = (key: string, labelHtml: string, unit?: string, extra: ResultRow[] = []): ResultCard => ({
    key,
    tone: null,
    rows: [{ labelHtml, valueHtml: unit ? `—${small(unit)}` : "—" }, ...extra],
  });
  return [
    [
      empty("statPill", t.layout),
      empty("fvRkVite", t.fvRkScrew, "kN"),
      empty("nef", t.nefAxial, undefined, [
        { labelHtml: t.nefShear + unitSpan("EC5 §8.3.1.2(4)"), valueHtml: "—" },
      ]),
      empty("fvRkUnione", t.fvRkUnion, "kN"),
    ],
    [
      empty("kmodGammaM", t.kmodGammaM),
      empty("forceF", t.fvEd, "kN"),
      empty("fvRdUnione", t.fvRdUnion, "kN"),
      empty("kser", t.kser + unitSpan(t.kserUnit), "kN/mm"),
      empty("verifica", t.shearCheck),
    ],
  ];
}

export function buildResultCards(state: CalcState, t: StatsT, texts: EngineTexts): [ResultCard[], ResultCard[]] {
  const withFa = state.faKN > 0;

  // "Layout viti", replaced by the combined load check (EC5 8.28) when Fa > 0
  let statPill: ResultCard;
  if (withFa && state.etaCombinata != null) {
    const ok = state.etaCombinata <= 1;
    statPill = {
      key: "statPill",
      tone: ok ? "ok" : "bad",
      rows: [{ labelHtml: t.combined, valueHtml: pct(t, state.etaCombinata, ok), formulaHtml: formulaCaricoCombinato(state) }],
    };
  } else {
    const allOk = state.grid.every((p) => p.ok) && (!state.gridB || state.gridB.every((p) => p.ok));
    statPill = {
      key: "statPill",
      tone: allOk ? "ok" : "bad",
      rows: [{ labelHtml: t.layout, valueHtml: allOk ? t.layoutOk : t.layoutBad }],
    };
  }

  const verificaOk = state.eta <= 1;
  const showSplit = (state.three || state.steel3) && state.etaSplitting != null && Number.isFinite(state.etaSplitting);
  const splitOk = !showSplit || (state.etaSplitting as number) <= 1;
  const verificaRows: ResultRow[] = [{ labelHtml: t.shearCheck, valueHtml: pct(t, state.eta, verificaOk) }];
  if (withFa && state.etaAxSimple != null) {
    verificaRows.push({ labelHtml: t.axialCheck, valueHtml: pct(t, state.etaAxSimple, state.etaAxSimple <= 1) });
  }
  if (showSplit) {
    verificaRows.push({
      labelHtml: t.splitCheck + unitSpan("EC5 §8.1.4"),
      valueHtml: pct(t, state.etaSplitting as number, splitOk),
    });
  }

  return [
    [
      statPill,
      {
        key: "fvRkVite",
        tone: null,
        rows: [
          {
            labelHtml: t.fvRkScrew,
            valueHtml: (state.resistenza.FvRk / 1000).toFixed(2) + small(`kN · ${governingLabel(texts, state.resistenza)}`),
            formulaHtml: formulaFvRkVite(state.resistenza, texts),
          },
        ],
      },
      {
        key: "nef",
        tone: null,
        rows: [
          {
            labelHtml: t.nefAxial,
            valueHtml:
              state.nef.toFixed(2) + " " + small(t.admissibleScrews(state.viteAmmissibili, state.viteTotali, state.twoSides)),
            formulaHtml: formulaNef(state, texts),
          },
          {
            labelHtml: t.nefShear + unitSpan("EC5 §8.3.1.2(4)"),
            valueHtml: state.nefShear.toFixed(2),
            formulaHtml: formulaNefShear(state, texts),
          },
        ],
      },
      {
        key: "fvRkUnione",
        tone: null,
        rows: [
          {
            labelHtml: t.fvRkUnion,
            valueHtml: (state.FvRkConnessione / 1000).toFixed(2) + small(t.totalKn),
            formulaHtml: formulaFvRkUnione(state, texts),
          },
        ],
      },
    ],
    [
      // k_mod · γM, replaced by Fax,Rd when Fa > 0
      withFa
        ? {
            key: "kmodGammaM",
            tone: null,
            rows: [
              {
                labelHtml: t.faxRd,
                valueHtml: (state.FaxRdUnione / 1000).toFixed(2) + small("kN"),
                formulaHtml: formulaFaxRdUnione(state),
              },
            ],
          }
        : {
            key: "kmodGammaM",
            tone: null,
            rows: [{ labelHtml: t.kmodGammaM, valueHtml: `${state.kmod.toFixed(2)} · ${state.gammaM.toFixed(2)}` }],
          },
      {
        key: "forceF",
        tone: null,
        rows: [
          {
            labelHtml: t.fvEd,
            valueHtml: state.forceF_kN.toFixed(1) + small("kN"),
            formulaHtml: formulaForceF(state),
          },
        ],
      },
      {
        key: "fvRdUnione",
        tone: null,
        rows: [
          {
            labelHtml: t.fvRdUnion,
            valueHtml: (state.FvRdUnione / 1000).toFixed(1) + small("kN"),
            formulaHtml: formulaFvRdUnione(state),
          },
        ],
      },
      {
        key: "kser",
        tone: null,
        rows: [
          {
            labelHtml: t.kser + unitSpan(t.kserUnit),
            valueHtml: state.kser != null ? (state.kser / 1000).toFixed(2) + small("kN/mm") : t.notAvailable,
            formulaHtml: formulaKser(state, texts),
          },
        ],
      },
      { key: "verifica", tone: verificaOk && splitOk ? "ok" : "bad", rows: verificaRows },
    ],
  ];
}
